import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import ThemeContext from "../context/ThemeContext";
import AuthContext from "../context/AuthContext";
import ArchiveContext from "../context/ArchiveContext";
import LanguageContext from "../context/LanguageContext";
import { getLocalizedTitle, getLocalizedDescription, getTextDirection } from "../utils/languageUtils";
import AddArchiveItem from "./AddArchiveItem";
import ReadMore from "./ReadMore";

export default function Archive() {
    const { theme } = useContext(ThemeContext);
    const { user } = useContext(AuthContext);
    const { language } = useContext(LanguageContext);
    const { archiveItems, loading, error, addArchiveItem, deleteArchiveItem, retryLoadArchiveItems } = useContext(ArchiveContext);
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ show: false, itemId: null, itemTitle: '' });
    
    const handleAddItem = () => {
        setShowAddForm(true);
    };
    
    const handleCancelAdd = () => {
        setShowAddForm(false);
    };
    
    const handleNewItemSubmit = async (newItem) => {
        const result = await addArchiveItem(newItem);
        
        if (result.success) {
            // Hide the form on success
            setShowAddForm(false);
            // Optionally show a success message
            console.log(result.message);
        } else {
            // Show error message
            alert(result.message);
        }
    };

    const handleRetry = () => {
        retryLoadArchiveItems();
    };

    const handleDeleteClick = (itemId, itemTitle) => {
        setDeleteDialog({ show: true, itemId, itemTitle });
    };

    const handleDeleteConfirm = async () => {
        const result = await deleteArchiveItem(deleteDialog.itemId);
        if (!result.success) {
            alert(result.message);
        }
        setDeleteDialog({ show: false, itemId: null, itemTitle: '' });
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ show: false, itemId: null, itemTitle: '' });
    };

    return (
        <div className="archive">
            {deleteDialog.show && (
                <>
                    <div className="delete-dialog__overlay" onClick={handleDeleteCancel} />
                    <div className="delete-dialog">
                        <h2 className="delete-dialog__title">Delete Archive Item</h2>
                        <p className="delete-dialog__message">
                            Are you sure you want to permanently delete "{deleteDialog.itemTitle}"? 
                            This action cannot be undone.
                        </p>
                        <div className="delete-dialog__buttons">
                            <button 
                                className="delete-dialog__button delete-dialog__button--cancel"
                                onClick={handleDeleteCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="delete-dialog__button delete-dialog__button--delete"
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className="archive__header">
                <h1 className="archive__title">Qajar Manuscripts Archive</h1>
                <p className="archive__description">
                    Explore our digital collection of manuscripts from the Qajar era, featuring works by prominent Iranian intellectuals and political figures.
                </p>
            </div>
            
            {loading ? (
                <div className="archive__loading">
                    <p>Loading archive items...</p>
                </div>
            ) : error ? (
                <div className="archive__error">
                    <div className="archive__error-content">
                        <h3>Unable to Load Archive Items</h3>
                        <p>{error}</p>
                        <button 
                            className="archive__retry-button"
                            onClick={handleRetry}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {user && (
                        <div className="archive__admin-section">
                            {!showAddForm ? (
                                <button 
                                    className="archive__add-button"
                                    onClick={handleAddItem}
                                >
                                    Add New Archive Item
                                </button>
                            ) : (
                                <AddArchiveItem 
                                    onAddItem={handleNewItemSubmit}
                                    onCancel={handleCancelAdd}
                                />
                            )}
                        </div>
                    )}
                    
                    <div className="archive__grid">
                        {archiveItems.length === 0 ? (
                            <div className="archive__empty">
                                <p>No archive items available yet.</p>
                                {user && (
                                    <p>You can add the first archive item using the button above.</p>
                                )}
                            </div>
                        ) : (
                            archiveItems.map(item => (
                                <div key={item.id} className="archive-item">
                                    <div className="archive-item__image-container">
                                        <img 
                                            src={item.image_url} 
                                            alt={getLocalizedTitle(item.title, item.persian_title, language)}
                                            className="archive-item__image"
                                            onError={(e) => {
                                                e.target.src = "/images/placeholder.jpg";
                                            }}
                                        />
                                    </div>
                                    <div className="archive-item__content">
                                        <div className="archive-item__header">
                                            <h2 
                                                className="archive-item__title"
                                                dir={getTextDirection(language)}
                                            >
                                                {getLocalizedTitle(item.title, item.persian_title, language)}
                                            </h2>
                                            {user && (
                                                <button 
                                                    className="archive-item__delete"
                                                    onClick={() => handleDeleteClick(item.id, getLocalizedTitle(item.title, item.persian_title, language))}
                                                    aria-label="Delete archive item"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                        <p className="archive-item__period">
                                            {item.start_year && item.end_year 
                                                ? `${item.start_year} - ${item.end_year}`
                                                : item.start_year || item.end_year || ''
                                            }
                                        </p>
                                        <ReadMore 
                                            text={getLocalizedDescription(item.description, item.persian_description, language)}
                                            maxHeight="4.8em"
                                            dir={getTextDirection(language)}
                                        />
                                        <div className="archive-item__footer">
                                            <span className="archive-item__count">
                                                {item.documentCount} {item.documentCount === 1 ? 'document' : 'documents'}
                                            </span>
                                            <Link 
                                                to={`/archive/${item.id}`} 
                                                className="archive-item__button"
                                            >
                                                View Documents
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
} 